import { useMemo } from "react"
import { getScenarioCostFacilityDetail } from "routes/scenarios/controller/scenario-cost-facility-detail"
import { FREQUENCY_LABELS, FREQUENCY_ORDER, FacilityDrillDownContent } from "./facility-drill-down-content"

// Database-backed drill-down: the server aggregates only this facility's PK range
// (scenarioCostFacilityDetail) instead of the browser filtering a full task array.
// Mounted only when the dialog opens, so the aggregate is fetched on demand.
export function FacilityDrillDownServerContent({ facility, currencySymbol, detailArgs, onClose }) {
    const { data, loading, error } = getScenarioCostFacilityDetail.useResults.status({
        ...detailArgs,
        facilityId: facility?.facilityId ?? "",
    })
    const criticalityBreakdown = useMemo(buildCriticalityBreakdown, [data])
    const frequencyBreakdown = useMemo(buildFrequencyBreakdown, [data])

    return (
        <FacilityDrillDownContent
            facility={facility}
            currencySymbol={currencySymbol}
            taskCount={data?.taskCount ?? 0}
            criticalityBreakdown={criticalityBreakdown}
            frequencyBreakdown={frequencyBreakdown}
            loading={loading && !data}
            // Without this, a stale generation or a rejected request would render as a
            // facility with zero tasks and no cost.
            error={error && !data ? error : null}
            onClose={onClose}
        />
    )

    function buildCriticalityBreakdown() {
        return (data?.byClassification ?? []).map(toEntry).sort(byCostDescending)

        function toEntry(group) {
            return { label: group.label, cost: group.annualCost, hours: group.annualHours, count: group.taskCount }
        }
    }

    function buildFrequencyBreakdown() {
        const byBucket = new Map((data?.byFrequency ?? []).map(keyByLabel))
        return FREQUENCY_ORDER.filter(hasBucket).map(toEntry)

        function keyByLabel(group) {
            return [group.label, group]
        }

        function hasBucket(key) {
            return byBucket.has(key)
        }

        function toEntry(key) {
            const group = byBucket.get(key)
            return {
                key,
                label: FREQUENCY_LABELS[key] || key,
                cost: group.annualCost,
                hours: group.annualHours,
                count: group.taskCount,
            }
        }
    }

    function byCostDescending(a, b) {
        return b.cost - a.cost
    }
}
